// TimeRange itself is generated from the shared schema. What lives here is the
// arithmetic over it, which a JSON Schema cannot express.
export function duration(range) {
    return range.endSec - range.startSec;
}
export function contains(range, seconds) {
    return seconds >= range.startSec && seconds < range.endSec;
}
export function overlaps(a, b) {
    return a.startSec < b.endSec && b.startSec < a.endSec;
}
/** Confine a range to `[0, mediaDurationSec]`, preserving its ordering. */
export function clamp(range, mediaDurationSec) {
    const startSec = Math.min(Math.max(range.startSec, 0), mediaDurationSec);
    const endSec = Math.min(Math.max(range.endSec, startSec), mediaDurationSec);
    return { startSec, endSec };
}
/** Merge overlapping or touching ranges into a minimal sorted set. */
export function mergeRanges(ranges) {
    if (ranges.length === 0)
        return [];
    const sorted = [...ranges].sort((a, b) => a.startSec - b.startSec);
    const merged = [];
    let current = sorted[0];
    for (let i = 1; i < sorted.length; i++) {
        const next = sorted[i];
        if (next.startSec <= current.endSec) {
            current = { startSec: current.startSec, endSec: Math.max(current.endSec, next.endSec) };
        }
        else {
            merged.push(current);
            current = next;
        }
    }
    merged.push(current);
    return merged;
}
/**
 * Widen a range outward to the nearest enclosing keyframes.
 *
 * This is what makes "drag a selection, download just that piece" cheap. A cut
 * that lands mid-GOP forces a re-encode, because the first frames of the output
 * reference picture data that is no longer there. Snapping the start *back* to
 * the preceding keyframe and the end *forward* to the following one lets the
 * extraction be a stream copy: seconds of work, no quality loss, no transcode
 * bill.
 *
 * The caller decides what to do with the extra frames — either accept the
 * slightly wider clip, or have the player trim them on playback.
 *
 * @param keyframesSec Ascending keyframe offsets, as recorded in the asset
 *                     manifest at ingest.
 */
export function snapToKeyframes(range, keyframesSec, mediaDurationSec) {
    if (keyframesSec.length === 0)
        return clamp(range, mediaDurationSec);
    const startIndex = lastIndexAtOrBefore(keyframesSec, range.startSec);
    const startSec = startIndex >= 0 ? keyframesSec[startIndex] : 0;
    const endIndex = firstIndexAtOrAfter(keyframesSec, range.endSec);
    const endSec = endIndex < keyframesSec.length ? keyframesSec[endIndex] : mediaDurationSec;
    return clamp({ startSec, endSec }, mediaDurationSec);
}
/** Binary search: greatest index whose value is <= `target`, or -1. */
function lastIndexAtOrBefore(sorted, target) {
    let low = 0;
    let high = sorted.length - 1;
    let found = -1;
    while (low <= high) {
        const mid = (low + high) >>> 1;
        if (sorted[mid] <= target) {
            found = mid;
            low = mid + 1;
        }
        else {
            high = mid - 1;
        }
    }
    return found;
}
/**
 * Binary search: least index whose value is >= `target`, or `sorted.length`.
 *
 * At-or-after rather than strictly-after, because ranges are half-open. A
 * selection ending at exactly 8.0s needs no frame from the GOP that starts
 * there, so a keyframe landing on the boundary is already the correct cut and
 * extending to the next one would hand back a second of footage nobody asked
 * for.
 */
function firstIndexAtOrAfter(sorted, target) {
    let low = 0;
    let high = sorted.length;
    while (low < high) {
        const mid = (low + high) >>> 1;
        if (sorted[mid] >= target) {
            high = mid;
        }
        else {
            low = mid + 1;
        }
    }
    return low;
}
/**
 * Render seconds as `HH:MM:SS.mmm`, the form used in URLs, filenames, and
 * anywhere a human reads a timecode.
 */
export function formatTimecode(seconds) {
    if (!Number.isFinite(seconds) || seconds < 0) {
        throw new RangeError(`cannot format timecode from ${seconds}`);
    }
    const totalMs = Math.round(seconds * 1000);
    const ms = totalMs % 1000;
    const totalSeconds = (totalMs - ms) / 1000;
    const s = totalSeconds % 60;
    const totalMinutes = (totalSeconds - s) / 60;
    const m = totalMinutes % 60;
    const h = (totalMinutes - m) / 60;
    const pad = (n, width = 2) => String(n).padStart(width, "0");
    return `${pad(h)}:${pad(m)}:${pad(s)}.${pad(ms, 3)}`;
}
/**
 * Parse the timecode forms people actually type: `93`, `1:33`, `1:33.5`,
 * `00:01:33.500`. Returns seconds.
 */
export function parseTimecode(input) {
    const trimmed = input.trim();
    if (!/^(\d+:){0,2}\d+(\.\d+)?$/.test(trimmed)) {
        throw new SyntaxError(`unrecognised timecode: ${JSON.stringify(input)}`);
    }
    const parts = trimmed.split(":").map(Number);
    return parts.reduce((total, part) => total * 60 + part, 0);
}
//# sourceMappingURL=time.js.map